import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { apiGet } from '../lib/api'

interface CuponDetail {
  id: string
  codigo: string
  descripcion: string
  tipo: 'percent' | 'fixed'
  valor: number
  minimo: number
  maxUsos: number | null
  usos: number
  validoDesde: string
  validoHasta: string
  activo: boolean
  aplicaA?: 'all' | 'category' | 'brand' | 'product'
  aplicaIds?: string[]
  bogoBuy?: number | null
  bogoGet?: number | null
  envioGratis?: boolean
  maxDescuento?: number | null
}

type ApplicableTo = 'all' | 'category' | 'brand' | 'product'

interface FormState {
  code: string
  description: string
  type: 'percent' | 'fixed'
  value: number
  min_order: number
  max_uses: string
  valid_from: string
  valid_until: string
  is_active: boolean
  applicable_to: ApplicableTo
  applicable_ids: string[]
  bogo_enabled: boolean
  bogo_buy: string
  bogo_get: string
  free_shipping: boolean
  max_discount: string
}

const empty: FormState = {
  code: '',
  description: '',
  type: 'percent',
  value: 0,
  min_order: 0,
  max_uses: '',
  valid_from: '',
  valid_until: '',
  is_active: true,
  applicable_to: 'all',
  applicable_ids: [],
  bogo_enabled: false,
  bogo_buy: '',
  bogo_get: '',
  free_shipping: false,
  max_discount: '',
}

export function CuponForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams({ strict: false }) as { id?: string }
  const idParam = params.id
  const isEdit = idParam && idParam !== 'nuevo'
  const [form, setForm] = useState<FormState>(empty)
  const [error, setError] = useState('')

  const detailQuery = useQuery<CuponDetail>({
    queryKey: ['cupon', idParam],
    queryFn: () => apiGet(`/api/admin/cupones/${idParam}`),
    enabled: !!isEdit,
  })

  useEffect(() => {
    if (detailQuery.data) {
      const d = detailQuery.data
      setForm({
        code: d.codigo,
        description: d.descripcion,
        type: d.tipo,
        value: d.valor,
        min_order: d.minimo,
        max_uses: d.maxUsos != null ? String(d.maxUsos) : '',
        valid_from: d.validoDesde,
        valid_until: d.validoHasta,
        is_active: d.activo,
        applicable_to: (d.aplicaA as ApplicableTo) ?? 'all',
        applicable_ids: d.aplicaIds ?? [],
        bogo_enabled: !!(d.bogoBuy && d.bogoGet),
        bogo_buy: d.bogoBuy ? String(d.bogoBuy) : '',
        bogo_get: d.bogoGet ? String(d.bogoGet) : '',
        free_shipping: d.envioGratis === true,
        max_discount: d.maxDescuento != null ? String(d.maxDescuento) : '',
      })
    }
  }, [detailQuery.data])

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((s) => ({ ...s, [k]: v }))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        code: form.code,
        description: form.description,
        type: form.type,
        value: Number(form.value),
        min_order: Number(form.min_order),
        max_uses: form.max_uses === '' ? null : parseInt(form.max_uses, 10),
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        is_active: form.is_active,
        applicable_to: form.applicable_to,
        applicable_ids: form.applicable_to === 'all' ? [] : form.applicable_ids,
        bogo_buy_quantity: form.bogo_enabled && form.bogo_buy
          ? parseInt(form.bogo_buy, 10)
          : null,
        bogo_get_quantity: form.bogo_enabled && form.bogo_get
          ? parseInt(form.bogo_get, 10)
          : null,
        free_shipping: form.free_shipping,
        max_discount: form.max_discount === '' ? null : Number(form.max_discount),
      }
      const url = isEdit ? `/api/admin/cupones/${idParam}` : '/api/admin/cupones'
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error guardando')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cupones'] })
      navigate({ to: '/cupones' })
    },
    onError: (e: Error) => setError(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.code.trim()) {
      setError('El código es obligatorio')
      return
    }
    // BOGO o envío gratis pueden valer por sí solos sin "value"
    const hasOtherBenefit = form.bogo_enabled || form.free_shipping
    if (!hasOtherBenefit && form.value <= 0) {
      setError('El valor debe ser mayor a 0 (o activa BOGO / envío gratis)')
      return
    }
    if (form.type === 'percent' && form.value > 100) {
      setError('Un descuento porcentual no puede superar 100%')
      return
    }
    if (form.applicable_to !== 'all' && form.applicable_ids.length === 0) {
      setError('Debes indicar al menos un ID al que aplicar el cupón')
      return
    }
    if (form.bogo_enabled) {
      const b = parseInt(form.bogo_buy, 10)
      const g = parseInt(form.bogo_get, 10)
      if (!b || !g || b < 1 || g < 1) {
        setError('BOGO requiere cantidades buy y get mayores a 0')
        return
      }
    }
    mutation.mutate()
  }

  const title = isEdit ? 'Editar cupón' : 'Nuevo cupón'

  if (isEdit && detailQuery.isLoading) {
    return (
      <>
        <Topbar title={title} />
        <div className="grid place-items-center p-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
        </div>
      </>
    )
  }

  return (
    <>
      <Topbar
        title={title}
        subtitle={isEdit ? `ID: ${idParam} — Usos: ${detailQuery.data?.usos ?? 0}` : 'Crear un nuevo cupón de descuento'}
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: '/cupones' })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </button>
        }
      />
      <form onSubmit={onSubmit} className="p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Datos del cupón</h2>
              <div className="space-y-4">
                <Field label="Código" required hint="Solo letras, números y guiones. Se guarda en mayúsculas.">
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => update('code', e.target.value.toUpperCase())}
                    required
                    maxLength={40}
                    placeholder="VERANO2026"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-sm uppercase outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Descripción">
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    maxLength={255}
                    placeholder="Descuento de verano para clientes nuevos"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Tipo" required>
                    <select
                      value={form.type}
                      onChange={(e) => update('type', e.target.value as 'percent' | 'fixed')}
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    >
                      <option value="percent">Porcentaje (%)</option>
                      <option value="fixed">Monto fijo (MXN)</option>
                    </select>
                  </Field>

                  <Field label={`Valor ${form.type === 'percent' ? '(%)' : '($)'}`} required>
                    <input
                      type="number"
                      value={form.value}
                      onChange={(e) => update('value', parseFloat(e.target.value) || 0)}
                      min={0}
                      step="0.01"
                      required
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    />
                  </Field>
                </div>

                <Field label="Monto mínimo de pedido (MXN)" hint="0 para sin mínimo">
                  <input
                    type="number"
                    value={form.min_order}
                    onChange={(e) => update('min_order', parseFloat(e.target.value) || 0)}
                    min={0}
                    step="0.01"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Usos máximos" hint="Vacío para usos ilimitados">
                  <input
                    type="number"
                    value={form.max_uses}
                    onChange={(e) => update('max_uses', e.target.value)}
                    min={1}
                    placeholder="Ilimitado"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Descuento máximo (MXN)" hint="Cap al monto descontado; vacío = sin tope">
                  <input
                    type="number"
                    value={form.max_discount}
                    onChange={(e) => update('max_discount', e.target.value)}
                    min={0}
                    step="0.01"
                    placeholder="Sin tope"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Aplicabilidad</h2>
              <div className="space-y-4">
                <div>
                  <span className="mb-2 block text-xs font-medium text-ink-700">¿A qué aplica el cupón?</span>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {(['all', 'category', 'brand', 'product'] as ApplicableTo[]).map((opt) => (
                      <label
                        key={opt}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                          form.applicable_to === opt
                            ? 'border-brand-500 bg-brand-50 text-brand-900'
                            : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="applicable_to"
                          value={opt}
                          checked={form.applicable_to === opt}
                          onChange={() => {
                            update('applicable_to', opt)
                            if (opt === 'all') update('applicable_ids', [])
                          }}
                          className="size-3.5"
                        />
                        {opt === 'all' && 'Todo el catálogo'}
                        {opt === 'category' && 'Por categoría'}
                        {opt === 'brand' && 'Por marca'}
                        {opt === 'product' && 'Por producto'}
                      </label>
                    ))}
                  </div>
                </div>

                {form.applicable_to !== 'all' && (
                  <Field
                    label={`IDs aplicables (${
                      form.applicable_to === 'category' ? 'categorías' :
                      form.applicable_to === 'brand' ? 'marcas' : 'productos'
                    })`}
                    hint="IDs numéricos separados por coma. Ej: 12, 45, 78"
                  >
                    <input
                      type="text"
                      value={form.applicable_ids.join(', ')}
                      onChange={(e) => {
                        const ids = e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter((s) => /^\d+$/.test(s))
                        update('applicable_ids', ids)
                      }}
                      placeholder="12, 45, 78"
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-brand-500"
                    />
                  </Field>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Tipo especial</h2>
              <div className="space-y-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.free_shipping}
                    onChange={(e) => update('free_shipping', e.target.checked)}
                    className="mt-0.5 size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-ink-900">Envío gratis</div>
                    <div className="text-xs text-ink-500">
                      Al aplicar, el costo de envío se reduce a $0.
                    </div>
                  </div>
                </label>

                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.bogo_enabled}
                    onChange={(e) => {
                      update('bogo_enabled', e.target.checked)
                      if (!e.target.checked) {
                        update('bogo_buy', '')
                        update('bogo_get', '')
                      }
                    }}
                    className="mt-0.5 size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-ink-900">BOGO (compra X, llévate Y gratis)</div>
                    <div className="text-xs text-ink-500">
                      Ej: buy=1 get=1 → 2x1. Calcula descuento automático sobre las unidades repetidas más baratas.
                    </div>
                  </div>
                </label>

                {form.bogo_enabled && (
                  <div className="ml-7 grid grid-cols-2 gap-3">
                    <Field label="Compra (buy)" required>
                      <input
                        type="number"
                        min={1}
                        value={form.bogo_buy}
                        onChange={(e) => update('bogo_buy', e.target.value)}
                        placeholder="1"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Lleva gratis (get)" required>
                      <input
                        type="number"
                        min={1}
                        value={form.bogo_get}
                        onChange={(e) => update('bogo_get', e.target.value)}
                        placeholder="1"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Vigencia</h2>
              <div className="space-y-4">
                <Field label="Válido desde">
                  <input
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => update('valid_from', e.target.value)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <Field label="Válido hasta">
                  <input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => update('valid_until', e.target.value)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => update('is_active', e.target.checked)}
                    className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  Cupón activo
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {mutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {isEdit ? 'Guardar cambios' : 'Crear cupón'}
              </button>
              <button
                type="button"
                onClick={() => navigate({ to: '/cupones' })}
                className="mt-2 w-full rounded-lg border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-700">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink-500">{hint}</p>}
    </div>
  )
}
